// import debounce from 'lodash.debounce';
import { useRef, useState, useContext, useEffect, type FC } from 'react';
import GoogleMapReact, { type MapOptions } from 'google-map-react';
import useSupercluster from 'use-supercluster';
import ClusterPin from '4-sections/FilteredListings/AssetsListing/Map/ClusterPin';
import ItemPin from './ItemPin';
import LocationListingContext, { type LocationListingContextProps } from '../LocationListingContext';
import { filterItemsById } from '../LocationListingHelpers';
import { type ImageProps } from '2-core-elements/Image/Image';
import Button from '2-core-elements/Buttons/Button';
import { BREAKPOINT_MD } from 'utils/breakpoint';

export interface Coordinates {
    lat: number;
    lon: number;
}

export interface Country {
    name: string;
    placeId: string;
}

export interface LocationMapItem {
    id: string;
    name: string;
    img?: Pick<ImageProps, 'imgSrc' | 'imgAlt' | 'imgHeight' | 'imgWidth'>;
    image?: { url: string; altText: string };
    description: string;
    coordinates: Coordinates;
    // countries: Country[];
    href: string;
}


interface MapProps {
    apiKey: string;
    mapId?: string;
    items: LocationMapItem[];
}

const DEFAULT_MAP_BOUNDS = [179.6443849291049, -166.72520913919715, 74.36780312326442, -67.5145600424235];
const MAP_MIN_ZOOM = 1.8;
const MOBILE_MAP_MIN_ZOOM = 0.6;
const MAP_MAX_ZOOM = 6;
const DEFAULT_MAP_CENTER = { lat: 23.989552997735814, lng: 20.39062363886964 };
// const DEBOUNCE_VALUE = 100;

const getDefaultMapZoom = (): number => {
    return window.innerWidth < 480 ? MOBILE_MAP_MIN_ZOOM : MAP_MIN_ZOOM;
};

const ListingMap: FC<MapProps> = ({
    apiKey,
    mapId,
    items,
}: MapProps): JSX.Element => {
    const {
        activeItemId,
        setActiveItemId,
    } = useContext(
        LocationListingContext
    ) as LocationListingContextProps;

    const [bounds, setBounds] = useState<number[] | null>(DEFAULT_MAP_BOUNDS);
    const [zoom, setZoom] = useState(getDefaultMapZoom());
    const [center, setCenter] = useState(DEFAULT_MAP_CENTER);
    const [animationClass, setAnimationClass] = useState('animation-on');

    // eslint-disable-next-line no-undef
    const mapRef = useRef<google.maps.Map>();
    const mapOptions: google.maps.MapOptions = {
        mapId,
        minZoom: MOBILE_MAP_MIN_ZOOM,
        maxZoom: MAP_MAX_ZOOM,
        restriction: {
            latLngBounds: {
                east: 179.9999,
                north: 85,
                south: -85,
                west: -179.9999,
            },
            // strictBounds: true
        },
    };

    const filteredItems = (!activeItemId) ?
        items :
        filterItemsById(items, activeItemId);

    const points = filteredItems.map(item => {
        return {
            // type: 'Feature',
            properties: { cluster: false },
            geometry: {
                type: 'Point',
                coordinates: [
                    item.coordinates.lon,
                    item.coordinates.lat
                ],
            },
            data: item,
        };
    });

    const { clusters, supercluster } = useSupercluster({
        points,
        bounds,
        zoom,
        options: { radius: 80, maxZoom: MAP_MAX_ZOOM },
    });

    useEffect(() => {
        if (!activeItemId) {
            // zoom out when no active pin only happen on desktop
            if (window.innerWidth >= BREAKPOINT_MD) {
                setZoom(getDefaultMapZoom());
                setCenter(DEFAULT_MAP_CENTER);
            }
            document.body.classList.remove('mobile-modal-opened');
        } else {
            document.body.classList.add('mobile-modal-opened');
        }
    }, [activeItemId]);

    useEffect(() => {
        if (activeItemId) {
            setAnimationClass('animation-off');
        } else {
            setAnimationClass('animation-on');
        }
    }, [filteredItems, activeItemId]);

    return (
        <div className={`listing-map ${animationClass}`}>
            <GoogleMapReact
                bootstrapURLKeys={{ key: apiKey }}
                defaultCenter={DEFAULT_MAP_CENTER}
                defaultZoom={getDefaultMapZoom()}
                yesIWantToUseGoogleMapApiInternals={true}
                onGoogleApiLoaded={({ map }): void => {
                    mapRef.current = map;
                    mapRef.current?.setClickableIcons(false);
                    setCenter(DEFAULT_MAP_CENTER);
                    // setZoom(getDefaultMapZoom());
                    setTimeout(() => {
                        setZoom(getDefaultMapZoom());
                    }, 300);
                }}
                zoom={zoom}
                center={center}
                debounced={false}
                // onTilesLoaded={onTilesLoaded}
                onChange={({ zoom: thisZoom, bounds: thisBounds, center: thisCenter }): void => {
                    setCenter(thisCenter);
                    setZoom(thisZoom);
                    setBounds([
                        thisBounds.nw.lng,
                        thisBounds.se.lat,
                        thisBounds.se.lng,
                        thisBounds.nw.lat
                    ]);
                }}
                options={mapOptions as MapOptions}
            >
                {!activeItemId &&
                    clusters.map((cluster, index) => {
                        const [longitude, latitude] =
                            cluster.geometry.coordinates;
                        const { cluster: isCluster, point_count: pointCount } =
                            cluster.properties;

                        const animateStyle = { '--animation-order': index };

                        if (isCluster) {
                            return (
                                <ClusterPin
                                    key={`cluster-${cluster.id}`}
                                    lat={latitude}
                                    lng={longitude}
                                    onClick={(): void => {
                                        const expansionZoom = Math.min(
                                            supercluster.getClusterExpansionZoom(
                                                cluster.id
                                            ),
                                            20
                                        );
                                        if (!mapRef?.current) {
                                            return;
                                        }
                                        // the order need to be zoom then pan, if it's inverted, it has bug zooming at the wrong place
                                        mapRef.current.setZoom(expansionZoom);
                                        mapRef.current.panTo({
                                            lat: latitude,
                                            lng: longitude,
                                        });
                                    }}
                                    style={animateStyle as React.CSSProperties}
                                >
                                    {pointCount}
                                </ClusterPin>
                            );
                        }

                        return (
                            <ItemPin
                                key={cluster.data.id}
                                lat={latitude}
                                lng={longitude}
                                onClick={(): void => {
                                    if (!mapRef.current) {
                                        return;
                                    }
                                    setActiveItemId(cluster.data.id);
                                }}
                                isActive={false}
                                style={animateStyle as React.CSSProperties}
                            />
                        );
                    })}
                {!!activeItemId &&
                    filteredItems.map(({ id, coordinates: { lat, lon } }) => {
                        return (
                            <ItemPin
                                key={id}
                                lat={lat}
                                lng={lon}
                                isActive={true}
                                onClick={(): void => {
                                    setActiveItemId('');
                                }}
                            />
                        );
                    })}
            </GoogleMapReact>
            <div className="listing-map-controls">
                <Button
                    skin="2" onlyIcon={true} prefixIcon="add" onClick={(): void => {
                        if (zoom < MAP_MAX_ZOOM) {
                            setZoom(currentZoom => {
                                return currentZoom + 0.6;
                            });
                        }
                    }}
                />
                <Button
                    skin="2" onlyIcon={true} prefixIcon="remove" onClick={(): void => {
                        if (zoom > getDefaultMapZoom()) {
                            setZoom(currentZoom => {
                                return currentZoom - 0.6;
                            });
                        }
                    }}
                />
            </div>
        </div>
    );
};

export default ListingMap;
